import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {UserProfile} from '@shared/models/user-profile.model';
import {BehaviorSubject, Observable} from 'rxjs';
import {PatientDataStore} from '@shared/models/patient-data.model';
import {StorageService} from '@core/src/public-api';

@Injectable({
  providedIn: 'root'
})
export class PatientService {
  PATIENT_ID_KEY = 'patientId';
  private _patientSubject: BehaviorSubject<PatientDataStore>;
  private _dataStore: { // This is where we will store our data in memory
    patient: UserProfile
    accountUser: UserProfile
  };
  private _patientId: number;

  constructor(
    private http: HttpClient,
    private storageService: StorageService
  ) {
    this._dataStore = {patient: null, accountUser: null};
    this._patientSubject = new BehaviorSubject<PatientDataStore>({} as PatientDataStore);
  }

  getPatientObservable(): Observable<PatientDataStore> {
    if (!this._dataStore?.patient) {
      this.getPatient();
    }

    if (!this._dataStore?.accountUser) {
      this.getPatientAccount();
    }

    return this._patientSubject.asObservable();
  }

  async getPatient(): Promise<void> {
    this._dataStore.patient = await this.getPatientInfo(this.getPatientId());
    this.refresh();
  }

  async getPatientInfo(patientId?: number): Promise<UserProfile> {
    const url = `/api/v1/patient`;
    let httpParams = new HttpParams();
    if (patientId) {
      httpParams = httpParams.set('patientId', patientId.toString());
    }

    return this.http.get<UserProfile>(url, {params: httpParams}).toPromise();
  }

  private refresh() {
    this._patientSubject.next({...this._dataStore});
  }

  async getPatientAccount(): Promise<void> {
    this._dataStore.accountUser = await this.getPatientInfo();
    this.refresh();
  }

  reloadPatient(): void {
    this.getPatient();
  }

  resetPatient(): void {
    this._dataStore.patient = null;
    this.refresh();
  }

  resetPatientStore(): void {
    this._dataStore = {patient: null, accountUser: null};
    this.refresh();
  }

  setPatientId(patientId: number): void {
    this._patientId = patientId;
    if (!patientId) {
      this.storageService.removeItem(this.PATIENT_ID_KEY, true);
      return;
    }

    this.storageService.setItem(this.PATIENT_ID_KEY, patientId.toString(), true);
  }

  getPatientId(): number {
    if (this._patientId) {
      return this._patientId;
    }

    this._patientId = parseInt(this.storageService.getItem(this.PATIENT_ID_KEY, true), 10) || null;
    return this._patientId;
  }
}
